if (typeof AtlasLinkAndPopup === "undefined") {
    window.AtlasLinkAndPopup = class AtlasLinkAndPopup extends HTMLElement {
        constructor() {
            super();
            this.linkBlock = null;
            this.popupBlock = null;
            this.popupId = null;
            this.triggerElement = null;
        }

        connectedCallback() {
            this.linkBlock = this.querySelector(".js-atlas-block-link");
            if (!this.linkBlock) {
                console.warn("AtlasLinkAndPopup: Link block not found");
                return;
            }

            const popupIdAttr = this.linkBlock.getAttribute("data-popup-id");
            if (popupIdAttr) {
                this.popupId = popupIdAttr.trim();
            }

            const linkElements = this.linkBlock.querySelectorAll(
                'a.atlas-link-trigger, button, [role="button"]'
            );

            this.triggerElement =
                linkElements.length > 0 ? linkElements[0] : this.linkBlock;

            this._multipleLinks = linkElements.length > 1;

            if (this.triggerElement === this.linkBlock) {
                this.linkBlock.style.cursor = "pointer";
            }

            if (this._multipleLinks) {
                linkElements.forEach((link) => {
                    link.addEventListener(
                        "click",
                        this.handleLinkClick.bind(this)
                    );
                });
            }

            if (!this.popupId) {
                this.popupBlock =
                    this.querySelector(".js-atlas-block-popup") ||
                    this.querySelector("._atlas-block-popup");

                if (!this.popupBlock) {
                    const popupsById = this.querySelectorAll(
                        '[id^="AtlasPopup-"]'
                    );
                    if (popupsById.length > 0) {
                        this.popupBlock = popupsById[0];
                    }
                }

                if (!this.popupBlock) {
                    const allPopups = this.querySelectorAll(
                        '[class*="popup"], [id*="Popup"], [class*="atlas-popup"]'
                    );
                    console.warn(
                        "AtlasLinkAndPopup: Popup not found in container. Found elements:",
                        allPopups
                    );
                    if (allPopups.length > 0) {
                        this.popupBlock = allPopups[0];
                        console.log(
                            "AtlasLinkAndPopup: Using first found popup element:",
                            this.popupBlock
                        );
                    }
                }
            } else {
                let raw = this.popupId.trim();
                let idPart;
                if (raw.startsWith("#")) {
                    const noHash = raw.slice(1);
                    idPart = noHash.startsWith("AtlasPopup-")
                        ? noHash
                        : `AtlasPopup-${noHash}`;
                } else {
                    idPart = raw.includes("AtlasPopup-")
                        ? raw
                        : `AtlasPopup-${raw}`;
                }

                const makeIdSelector = (id) => {
                    if (
                        typeof CSS !== "undefined" &&
                        typeof CSS.escape === "function"
                    ) {
                        return `#${CSS.escape(id)}`;
                    }
                    return `[id="${id.replace(/"/g, '\\"')}"]`;
                };

                const safeSelector = makeIdSelector(idPart);

                this.popupBlock = document.querySelector(safeSelector);

                if (!this.popupBlock) {
                    this.popupBlock = document.querySelector(
                        `${safeSelector}.js-atlas-block-popup, ${safeSelector}._atlas-block-popup`
                    );
                }

                if (!this.popupBlock) {
                    console.warn(
                        "AtlasLinkAndPopup: Popup not found with id:",
                        this.popupId,
                        "- falling back to container search"
                    );
                    this.popupBlock =
                        this.querySelector(".js-atlas-block-popup") ||
                        this.querySelector("._atlas-block-popup");

                    if (!this.popupBlock) {
                        const popupsById = this.querySelectorAll(
                            '[id^="AtlasPopup-"]'
                        );
                        if (popupsById.length > 0) {
                            this.popupBlock = popupsById[0];
                        }
                    }
                }
            }

            if (!this.popupBlock) {
                console.warn(
                    "AtlasLinkAndPopup: Popup block not found",
                    this.popupId ? `with id: ${this.popupId}` : "in container"
                );
                if (
                    !this._multipleLinks &&
                    this.triggerElement &&
                    this.triggerElement.tagName === "A"
                ) {
                    this.triggerElement.addEventListener(
                        "click",
                        (e) => {
                            const href =
                                this.triggerElement.getAttribute("href") || "";
                            if (
                                href === "#" ||
                                href.trim() === "" ||
                                href.startsWith("#")
                            ) {
                                e.preventDefault();
                            }
                        },
                        { once: true }
                    );
                }
                return;
            }

            if (!this._multipleLinks) {
                this.triggerElement.addEventListener(
                    "click",
                    this.handleLinkClick.bind(this)
                );
            }

            this.initPopup();
        }

        initPopup() {
            if (!this.popupBlock.classList.contains("atlas-popup-modal")) {
                this.popupBlock.classList.add("atlas-popup-modal");
            }

            this.popupBlock.addEventListener("keyup", (event) => {
                if (event.code.toUpperCase() === "ESCAPE") {
                    this.hidePopup();
                }
            });

            this.popupBlock.addEventListener("click", (event) => {
                if (event.target === this.popupBlock) {
                    this.hidePopup();
                }
            });

            const closeButton = this.popupBlock.querySelector(
                '[id^="AtlasPopupClose-"]'
            );
            if (closeButton) {
                closeButton.addEventListener("click", () => this.hidePopup());
            }

            this.initSwipeToClose();
        }

        // Swipe-down-to-close for the mobile bottom-sheet popup (quick view).
        // Only active below the 1023px bottom-sheet breakpoint (see
        // atlas.block-popup.css) and only while the popup's own scrollable
        // content is already at the top, so it never fights normal scrolling
        // or the image carousel's horizontal drag.
        //
        // Several "More details" buttons across the box share the same
        // popup (one <atlas-link-and-popup> per button, all pointing at the
        // same #AtlasPopup-... via data-popup-id), so guard against wiring
        // this up more than once on the same container.
        initSwipeToClose() {
            const container = this.popupBlock.querySelector(
                ".atlas-popup-modal__container"
            );
            if (!container || container.dataset.swipeToCloseInit) return;
            container.dataset.swipeToCloseInit = "true";

            const contentWrapper = this.popupBlock.querySelector(
                ".atlas-popup-modal__content-wrapper"
            );

            const isBottomSheet = () =>
                window.matchMedia("(max-width: 1023px)").matches &&
                !!this.popupBlock.querySelector("[data-skio-quick-view-content]");

            // Prevent the browser from claiming the vertical gesture for its
            // own scroll/rubber-band before our touchmove handler gets a
            // chance to decide it's a close-drag (touch-action: auto lets
            // Safari start native scrolling on the very first move, after
            // which preventDefault() in JS no longer has any effect). Only
            // disabled while already scrolled to the top, so normal scrolling
            // deeper in the popup is unaffected.
            const syncTouchAction = () => {
                const atTop = !contentWrapper || contentWrapper.scrollTop <= 0;
                container.style.touchAction = atTop ? "pan-x" : "";
            };
            syncTouchAction();
            contentWrapper?.addEventListener("scroll", syncTouchAction, {
                passive: true
            });

            let startX = 0;
            let startY = 0;
            let deltaY = 0;
            let dragging = false;
            let decided = false;
            let verticalDrag = false;

            const reset = () => {
                dragging = false;
                decided = false;
                verticalDrag = false;
            };

            const finishDrag = (shouldClose) => {
                container.style.transition = "transform 0.25s ease";
                container.style.transform = shouldClose
                    ? "translateY(100%)"
                    : "";

                const cleanup = () => {
                    container.style.transition = "";
                    container.style.transform = "";
                    if (shouldClose) this.hidePopup();
                };
                container.addEventListener("transitionend", cleanup, {
                    once: true
                });
                setTimeout(cleanup, 300);
            };

            container.addEventListener(
                "touchstart",
                (event) => {
                    if (!isBottomSheet()) return;
                    if (contentWrapper && contentWrapper.scrollTop > 0) return;

                    const touch = event.touches[0];
                    startX = touch.clientX;
                    startY = touch.clientY;
                    deltaY = 0;
                    dragging = true;
                    decided = false;
                    verticalDrag = false;
                },
                { passive: true }
            );

            container.addEventListener(
                "touchmove",
                (event) => {
                    if (!dragging) return;
                    const touch = event.touches[0];
                    const dx = touch.clientX - startX;
                    const dy = touch.clientY - startY;

                    if (!decided) {
                        if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return;
                        decided = true;
                        verticalDrag = dy > 0 && Math.abs(dy) > Math.abs(dx);
                        if (!verticalDrag) {
                            dragging = false;
                            return;
                        }
                        container.style.transition = "none";
                    }
                    if (!verticalDrag) return;

                    deltaY = Math.max(0, dy);
                    event.preventDefault();
                    container.style.transform = `translateY(${deltaY}px)`;
                },
                { passive: false }
            );

            container.addEventListener("touchend", () => {
                if (!dragging || !verticalDrag) {
                    reset();
                    return;
                }
                finishDrag(deltaY > 80);
                reset();
            });

            container.addEventListener("touchcancel", reset);
        }

        handleLinkClick(event) {
            const el = event.currentTarget || this.triggerElement;

            if (el && el.tagName === "A" && el.hasAttribute("href")) {
                const href = el.getAttribute("href");

                if (
                    href &&
                    href.trim() !== "" &&
                    href !== "#" &&
                    !href.startsWith("#") &&
                    !href.startsWith("javascript:")
                ) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
            }

            this.showPopup();
        }

        showPopup() {
            if (!this.popupBlock) return;

            // Reopening mid-close (is-closing still pending its own
            // finishClose cleanup) must cancel that pending close outright —
            // otherwise its delayed finishClose() would fire afterwards and
            // rip focus/scroll-lock back off the popup we just (re)opened.
            this._closeToken = (this._closeToken || 0) + 1;
            this.popupBlock.classList.remove("is-closing");

            this.popupBlock.openedBy = this.triggerElement;
            document.body.classList.add("overflow-hidden");
            this.popupBlock.setAttribute("open", "");

            const dialog = this.popupBlock.querySelector('[role="dialog"]');
            if (dialog) {
                this.trapFocus(dialog);
            }

            window.pauseAllMedia?.();
        }

        hidePopup() {
            if (!this.popupBlock) return;
            if (!this.popupBlock.hasAttribute("open")) return;
            if (this.popupBlock.classList.contains("is-closing")) return;

            document.body.dispatchEvent(new CustomEvent("modalClosed"));
            window.pauseAllMedia?.();

            // Trigger the closing transition via a class instead of removing
            // [open] outright — some other rules key off [open] alone (e.g.
            // atlas.block-link.css hides slider nav while a popup is open),
            // and removing it immediately would drop those before the popup
            // has actually finished animating away. The attribute itself
            // (plus scroll-lock/focus, which fighting the animation with a
            // layout reflow mid-transition) is only cleared once the
            // transition has actually finished, with a timeout fallback in
            // case it never fires (e.g. reduced-motion, no property change).
            this.popupBlock.classList.add("is-closing");

            const container = this.popupBlock.querySelector(
                ".atlas-popup-modal__container"
            );
            const openedBy = this.popupBlock.openedBy;
            this._closeToken = (this._closeToken || 0) + 1;
            const token = this._closeToken;
            let finished = false;

            const finishClose = () => {
                if (finished || token !== this._closeToken) return;
                finished = true;
                container?.removeEventListener("transitionend", finishClose);
                this.popupBlock.classList.remove("is-closing");
                this.popupBlock.removeAttribute("open");
                document.body.classList.remove("overflow-hidden");
                this.removeTrapFocus(openedBy);
            };

            if (container) {
                container.addEventListener("transitionend", finishClose);
                setTimeout(finishClose, 500);
            } else {
                finishClose();
            }
        }

        trapFocus(container) {
            if (typeof trapFocus === "function") {
                trapFocus(container, container);
                return;
            }

            const elements = this.getFocusableElements(container);
            if (elements.length === 0) return;

            const first = elements[0];
            const last = elements[elements.length - 1];

            const keydownHandler = (event) => {
                if (event.code.toUpperCase() !== "TAB") return;

                if (event.target === last && !event.shiftKey) {
                    event.preventDefault();
                    first.focus();
                }

                if (
                    (event.target === container || event.target === first) &&
                    event.shiftKey
                ) {
                    event.preventDefault();
                    last.focus();
                }
            };

            document.addEventListener("keydown", keydownHandler);
            first.focus();

            this._keydownHandler = keydownHandler;
        }

        removeTrapFocus(elementToFocus = null) {
            if (typeof removeTrapFocus === "function") {
                removeTrapFocus(elementToFocus);
                return;
            }

            if (this._keydownHandler) {
                document.removeEventListener("keydown", this._keydownHandler);
                this._keydownHandler = null;
            }

            if (elementToFocus) {
                elementToFocus.focus();
            }
        }

        getFocusableElements(container) {
            const focusableSelectors = [
                "a[href]",
                "button:not([disabled])",
                "textarea:not([disabled])",
                "input:not([disabled])",
                "select:not([disabled])",
                '[tabindex]:not([tabindex="-1"])'
            ].join(", ");

            return Array.from(
                container.querySelectorAll(focusableSelectors)
            ).filter(
                (el) =>
                    !el.hasAttribute("disabled") &&
                    !el.hasAttribute("aria-hidden")
            );
        }
    };

    if (!customElements.get("atlas-link-and-popup")) {
        customElements.define("atlas-link-and-popup", window.AtlasLinkAndPopup);
    }
} else {
    if (!customElements.get("atlas-link-and-popup")) {
        customElements.define("atlas-link-and-popup", window.AtlasLinkAndPopup);
    }
}

// Fancybox's image zoom is a global overlay with no awareness of the
// popup it was opened from — nothing closes the popup underneath it, so
// they end up stacked. Called from atlas.media.js when a Fancybox
// instance opens.
window.AtlasLinkAndPopup.hideOpenPopups = function () {
    document.querySelectorAll("atlas-link-and-popup").forEach((el) => {
        if (el.popupBlock?.hasAttribute("open")) {
            el.hidePopup();
        }
    });
};
